import { type ReactNode } from 'react';

import { cn } from '../lib/cn.js';

import { Button } from './button.js';
import { ColumnHint } from './column-hint.js';
import { Skeleton } from './skeleton.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './table.js';

export interface DataTableColumn<Row> {
  id: string;
  header: ReactNode;
  /** Shown when the heading is hovered, unless the org has turned column explanations off. */
  description?: string;
  cell: (row: Row) => ReactNode;
  /** Tailwind class applied to both the header cell and body cell. */
  className?: string;
  /** Set true on the first column to enable horizontal-scroll sticky behavior. */
  sticky?: boolean;
}

export interface DataTableProps<Row> {
  columns: DataTableColumn<Row>[];
  rows: Row[];
  /** Row key extractor — required so React doesn't rely on row index. */
  rowKey: (row: Row) => string;
  isLoading?: boolean;
  /** Number of skeleton rows during loading. */
  loadingRowCount?: number;
  /** Content rendered when rows.length === 0 and isLoading is false. */
  empty?: ReactNode;
  /** Optional handler — when set, rows become clickable. */
  onRowClick?: (row: Row) => void;
  /** Cursor returned by the API for the next page. */
  nextCursor?: string | null;
  /** Fired when the user clicks "Load more". */
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  /** Custom row-className resolver (e.g. dim soft-deleted rows). */
  rowClassName?: (row: Row) => string | undefined;
  className?: string;
}

/**
 * Opinionated wrapper around `Table` that handles loading, empty, and
 * cursor-pagination states. Phase 1 lists (buildings, imports) use it
 * directly; later phases extend with sorting via column flags.
 */
export function DataTable<Row>({
  columns,
  rows,
  rowKey,
  isLoading,
  loadingRowCount = 6,
  empty,
  onRowClick,
  nextCursor,
  onLoadMore,
  isLoadingMore,
  rowClassName,
  className,
}: DataTableProps<Row>) {
  const showSkeletons = isLoading && rows.length === 0;
  const showEmpty = !isLoading && rows.length === 0;

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div className="overflow-hidden rounded-lg border border-border bg-surface">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((col) => (
                <TableHead key={col.id} className={col.className}>
                  <ColumnHint description={col.description}>{col.header}</ColumnHint>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {showSkeletons
              ? Array.from({ length: loadingRowCount }).map((_, i) => (
                  <TableRow key={`skeleton-${i}`}>
                    {columns.map((col) => (
                      <TableCell key={col.id} className={col.className}>
                        <Skeleton className="h-4 w-3/4" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : rows.map((row) => (
                  <TableRow
                    key={rowKey(row)}
                    className={cn(
                      onRowClick ? 'cursor-pointer' : undefined,
                      rowClassName?.(row),
                    )}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                  >
                    {columns.map((col) => (
                      <TableCell key={col.id} className={col.className}>
                        {col.cell(row)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
          </TableBody>
        </Table>
        {showEmpty ? (
          <div className="border-t border-border p-6 text-center text-sm text-fg-secondary">
            {empty ?? 'No results.'}
          </div>
        ) : null}
      </div>
      {nextCursor && onLoadMore ? (
        <div className="flex justify-center">
          <Button
            variant="outline"
            size="sm"
            onClick={onLoadMore}
            disabled={isLoadingMore}
          >
            {isLoadingMore ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
